import type { OrderBrief } from "./api/types";

/**
 * Where a shop order is, in the five groups the team works by: the sheet's CRM
 * tab asked the same questions — has it been paid, has it gone out, has it
 * arrived — and was filled in by hand.
 */
export type OrderStage = "unpaid" | "to_ship" | "shipping" | "delivered" | "closed";

export const ORDER_STAGES: Array<{ key: OrderStage; label: string; title: string }> = [
  { key: "unpaid", label: "待付款", title: "开了单还没付款" },
  { key: "to_ship", label: "待发货", title: "付了款，还没发出去" },
  { key: "shipping", label: "运送中", title: "已经发货，快递还没送到" },
  { key: "delivered", label: "已送达", title: "快递说送到了" },
  { key: "closed", label: "取消/退款", title: "付款失败、过期、取消或退了款" },
];

/**
 * Cash on delivery is paid last, to the courier or through Shopee: until the
 * money is in, where the parcel is says where the order is. Counted as 待付款 it
 * sat in 催付款, and CS were asked to chase people whose parcel was on the road.
 */
export function isCOD(o: Pick<OrderBrief, "payment_status" | "payment_method">): boolean {
  return o.payment_status === "pending" && o.payment_method === "cod";
}

/** A cash-on-delivery parcel that arrived and whose money nobody has confirmed yet. */
export function codToConfirm(o: Pick<OrderBrief, "payment_status" | "payment_method" | "fulfillment_status">): boolean {
  return isCOD(o) && o.fulfillment_status === "delivered";
}

type StageOf = Pick<OrderBrief, "payment_status" | "fulfillment_status"> & Partial<Pick<OrderBrief, "payment_method">>;

export function orderStage(o: StageOf): OrderStage {
  const cod = isCOD(o);
  if (o.payment_status === "pending" && !cod) return "unpaid";
  if (o.payment_status !== "paid" && !cod) return "closed"; // failed | expired | refunded
  switch (o.fulfillment_status) {
    case "shipped":
      return "shipping";
    case "delivered":
      return "delivered";
    case "cancelled":
      return "closed";
    default:
      return "to_ship"; // unfulfilled | processing
  }
}

const PAY: Record<string, string> = { pending: "待付款", failed: "付款失败", expired: "已过期", refunded: "已退款" };
const SHIP: Record<string, string> = {
  unfulfilled: "待发货",
  processing: "备货中",
  shipped: "已发货",
  delivered: "已送达",
  cancelled: "已取消",
};

/** The order's own word: the payment while unpaid, the parcel once paid (or on its way, cash on delivery).
 *  Cancelled is cancelled: cp writes 「failed」 on the payment of an order it calls off, and a cash-on-delivery
 *  order cancelled in cp read 付款失败 here (owner, 2026-10-11). Money given back still says 已退款. */
export function orderWord(o: StageOf): string {
  if (o.fulfillment_status === "cancelled" && o.payment_status !== "refunded") return "已取消";
  return o.payment_status === "paid" || isCOD(o)
    ? (SHIP[o.fulfillment_status] ?? o.fulfillment_status)
    : (PAY[o.payment_status] ?? o.payment_status);
}

/** 「Wei Jian 取消：客户不要了」 from the shop's 「Cancelled by Wei Jian: 客户不要了」; a sweep's own sentence as it is. */
export function cancelWhy(reason?: string): string {
  const r = (reason ?? "").trim();
  const m = /^Cancelled by (.+?): ([\s\S]+)$/.exec(r);
  return m ? `${m[1]} 取消：${m[2]}` : r;
}

/** Just the parcel, for the orders view's 物流 column. */
export function shipWord(o: StageOf): string {
  if (o.payment_status !== "paid" && !isCOD(o)) return "";
  return SHIP[o.fulfillment_status] ?? o.fulfillment_status;
}

const METHOD: Record<string, string> = {
  bank_transfer: "银行转账",
  duitnow: "DuitNow",
  cash: "现金",
  ewallet: "电子钱包",
  cod: "货到付款",
  other: "其他",
};

/** How it was paid, in cp's words; empty is the online payment page. */
export function paymentMethodLabel(m?: string): string {
  if (!m) return "网上付款";
  return METHOD[m] ?? m;
}

/** Where the warehouse system has the order, in a line: its number there, or that it never went. */
export function wmsLine(o: Pick<OrderBrief, "wms_ref" | "wms_state">): string {
  if (!o.wms_ref || o.wms_state === "cancelled") return "没进仓库系统";
  const state: Record<string, string> = { pending: "排队推送", pushed: "仓库已收到", shipped: "仓库已出货", failed: "推送失败" };
  return `仓库单 ${o.wms_ref}${state[o.wms_state ?? ""] ? " · " + state[o.wms_state ?? ""] : ""}`;
}

/**
 * 回购: the orders a customer placed after their first paid one — the sheet's
 * 「Repeat Purchase」. A cancelled, failed, lapsed or refunded order is no sale
 * and no repeat.
 */
export function repeatOrders(orders: OrderBrief[]): Set<string> {
  const live = (o: OrderBrief) => !["failed", "expired", "refunded"].includes(o.payment_status) && o.fulfillment_status !== "cancelled";
  const at = (o: OrderBrief) => Date.parse(o.ordered_at || o.paid_at || "") || 0;
  const firstPaid = new Map<string, number>();
  for (const o of orders) {
    if (!o.wa_id || o.payment_status !== "paid" || !live(o)) continue;
    const t = at(o);
    if (!firstPaid.has(o.wa_id) || t < firstPaid.get(o.wa_id)!) firstPaid.set(o.wa_id, t);
  }
  const out = new Set<string>();
  for (const o of orders) {
    const first = o.wa_id ? firstPaid.get(o.wa_id) : undefined;
    if (first !== undefined && live(o) && at(o) > first) out.add(o.order_no);
  }
  return out;
}

/** RM158 for a whole amount, RM109.50 otherwise; thousands with a comma, as 今天 writes them (「RM4072」, QA V-12, 10/27). */
export function rm(total: number): string {
  return `RM${total.toLocaleString("en-MY", Number.isInteger(total) ? {} : { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** A Singapore number as WhatsApp writes it: 65 and eight digits. A Messenger id is no number. */
export const isSingaporeNumber = (n: string) => !n.startsWith("fb:") && /^65\d{8}$/.test(n.replace(/\D/g, ""));

/**
 * Shipped to Singapore, as the shop records it. An API that does not send the
 * country yet leaves only the number to go by.
 */
export function isSingapore(o: Pick<OrderBrief, "country" | "phone" | "wa_id">): boolean {
  return o.country ? o.country === "SG" : isSingaporeNumber(o.phone || o.wa_id || "");
}

/**
 * Which part of the money an order is, one word for one thing on every page (10/24):
 *  - paid 已收: the money is in;
 *  - owed 未收: cash on delivery nobody has confirmed the money of, wherever the
 *    parcel is (还没寄、在路上、送到了) — sold, part of 营业额, not in yet;
 *  - due 待付款: opened and not paid — no sale yet, not 营业额.
 * 要收的钱 is none of these: it is 客户表's list of money to act on (CustomerMoney).
 * A cancelled, failed, lapsed or refunded order is no money at all.
 */
export type MoneyPart = "paid" | "owed" | "due";

export function moneyPart(o: StageOf): MoneyPart | null {
  if (orderStage(o) === "closed") return null;
  if (o.payment_status === "paid") return "paid";
  return isCOD(o) ? "owed" : "due";
}

/**
 * What a list of orders comes to, part by part, and how many orders each. A
 * part refund counts at what the shop kept, as 业绩 does (owner, 2026-10-05:
 * 「退钱部分退钱营业额要对」). Added in sen, so a column of RM x.90 comes out
 * exact. 订单's head and its cards are this one sum.
 */
export function orderMoney(orders: readonly OrderBrief[]): Record<MoneyPart, number> & { n: Record<MoneyPart, number> } {
  const sen = { paid: 0, owed: 0, due: 0 };
  const n = { paid: 0, owed: 0, due: 0 };
  for (const o of orders) {
    const part = moneyPart(o);
    if (!part) continue;
    sen[part] += Math.max(Math.round(o.total * 100) - Math.round((o.refunded ?? 0) * 100), 0);
    n[part] += 1;
  }
  return { paid: sen.paid / 100, owed: sen.owed / 100, due: sen.due / 100, n };
}

/** RM4,523.40 — a sum to the sen, with the thousands marked, as the report shows money. */
export function rmSum(total: number): string {
  return `RM${total.toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** The order in cp, when the settings name the shop admin. */
export function orderLink(shopURL: string, orderNo: string): string {
  return shopURL ? `${shopURL}/#/orders/${encodeURIComponent(orderNo)}` : "";
}

/** cp's new-order page, for money that came in with no order behind it. */
export function newOrderLink(shopURL: string): string {
  return shopURL ? `${shopURL}/#/orders/new` : "";
}
